<template>
	<ui-root v-if="loaded" :data="cats" :part="form" ref="frm" :errs="errors" :actions="dialogControls(env)"></ui-root>
</template>

<script setup lang="ts">
import {onMounted, onUnmounted, ref, watch, reactive} from "vue";
import {injectEnv, injectEventBus, provideNewEventBus} from "@fd/Environment";
import type {Cats} from "@t/list/Category";
import type {WithMeta} from "@t/fd/WithMeta";
import {dialogControls, FormDefinition} from "@t/fd/layout/FormDefinition";
import FormPart from "@fd/containers/FormPart.vue";
import type {ErrorMap} from "@fd/PartInjectKeys";
import {NetworkResponse} from "@fd/Types";
import {useRouter, useRoute} from "vue-router";
import {useNavigationStore} from "@/store/NavigationStore.ts";
import {InterfaceRoot} from "@t/fd/InterfaceRoot.ts";
import UiRoot from "@fd/containers/UiRoot.vue";

const props = defineProps<{
	/** ID of categories (customer / task / event / content) */
	uid: string
}>();

const env = injectEnv();
const navStore = useNavigationStore();
const router = useRouter();

const loaded = ref<boolean>(false);

const errors = reactive<ErrorMap>([]);
const cats = ref<Cats|null>(null);
const form = ref<InterfaceRoot<FormDefinition>|null>(null);
const frm = ref(null);

function receivedData(data:Cats & WithMeta):void {
	if (data.META) {
		form.value = data.META.form;
		delete data.META;
	}
	cats.value = data;
	loaded.value = true;
}

function loadData() {
	env.net.fetch("api/cats/"+props.uid+"?meta=true")
		.then((result) => result.json())
		.then(receivedData);
}

function saveData():Promise<NetworkResponse> {
    const d = cats.value;
    return env.net.fetch("api/cats/"+d.id, "PUT", d);
}

const bus = provideNewEventBus();
const unregCommands = bus.onCommand(["save", "saveAndBack"], async (cmd) => {
	errors.length = 0;
	const response = await saveData();
	if (response.ok) {
		if (cmd === "save") {
			const data = await response.json();
			receivedData(data);
			if (frm.value) {
				frm.value.setDirty(false);
			}
		} else {
			// Go back
			router.back();
		}
		return "ok";
	} else {
		// TODO process errors
		// Categories don't have a validation (there is nothing to mess up)
		console.error(response);
		return "fail";
	}
});


onMounted(() => {
	loadData();
	navStore.setStandardMenu();
	let titleKey;
	switch (props.uid) {
		case "custcats":
			titleKey = "title.profile.custCats";
			break;
		case "contentcats":
			titleKey = "title.profile.contentCats";
			break;
		case "eventcats":
			titleKey = "title.profile.eventCats";
			break;
		case "taskcats":
			titleKey = "title.profile.taskCats";
			break;
	}
	navStore.forward(env.msg(titleKey));
});
watch(() => props.uid, (n) => loadData());

onUnmounted(() => {
	unregCommands();
});
</script>

<style>

</style>